import { zodResolver } from '@hookform/resolvers/zod'
import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { useSpkFarmData } from '../../../data/useSpkFarmData'
import { createTxOverview, TxOverview } from './createTxOverview'
import { validationIssueToMessage } from './validation'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { StakeObjective } from '@/features/actions/flavours/stake/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import {
  getFieldsForTransferFromUserForm,
  useDebouncedFormValues,
} from '@/features/dialogs/common/logic/transfer-from-user/form'
import { getTransferFromUserFormValidator } from '@/features/dialogs/common/logic/transfer-from-user/validation'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'

export interface UseStakeDialogResult {
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  stakedToken: TokenWithValue
  sacrificesYield: boolean
  pageStatus: PageStatus
  txOverview: TxOverview
  actionsViewState: ActionsViewState
}

export function useStakeDialog(): UseStakeDialogResult {
  const chainId = useChainId()
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { account } = useConnectedAddress()

  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spkFarmData = useSpkFarmData({ chainId, tokenRepository })

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getTransferFromUserFormValidator({
        tokenRepository,
        issueToMessage: validationIssueToMessage,
      }),
    ),
    defaultValues: {
      symbol: spkFarmData.stakingToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: StakeObjective[] = [
    {
      type: 'stake',
      amount: formValues.value,
      stakingToken: formValues.token,
      rewardToken: spkFarmData.rewardToken,
      farm: spkFarmData.farmAddress,
    },
  ]

  const txOverview = createTxOverview({
    spkFarmData,
    formValues,
  })

  const sacrificesYield =
    formValues.token.symbol === TokenSymbol('sDAI') || formValues.token.symbol === TokenSymbol('sUSDS')

  const stakedToken: TokenWithValue = {
    token: spkFarmData.stakingToken,
    value: txOverview.status === 'success' ? txOverview.outcome.value : NormalizedNumber.ZERO,
  }

  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  return {
    selectableAssets: [
      {
        token: spkFarmData.stakingToken,
        balance: tokenRepository.findOneBalanceOrZero(spkFarmData.stakingToken.symbol),
      },
    ],
    assetsFields: getFieldsForTransferFromUserForm({ form, tokenRepository }),
    form,
    stakedToken,
    sacrificesYield,
    txOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    actionsViewState,
  }
}
